// Admin: delivery monitoring for paid orders
import apiClient from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import { getErrorMessage } from '../utils/apiError';

const fulfillmentService = {
  /**
   * Paid orders that aren't fully delivered, each with status, issues (who can fix them) and a
   * plain-language "whatToDo".
   * GET /api/fulfillment/attention?limit -> { count, needsAdmin, waitingOnCustomer, items: [{ orderId, id, customer, email,
   * phone, gitHubUsername, products: [name], amount, paidAt, status, attempts, lastAttemptAt, nextAttemptAt,
   * issues: [{ code, message, actionBy, howToFix }], actionBy: 'admin' | 'customer', whatToDo }] }
   */
  getNeedsAttention: async (limit = 100) => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.FULFILLMENT.ATTENTION, { params: { limit } });
      return { success: true, data: response.data?.data };
    } catch (error) {
      return { success: false, error: getErrorMessage(error, 'Could not load delivery issues') };
    }
  },

  getOrderStatus: async orderId => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.FULFILLMENT.ORDER(orderId));
      return { success: true, data: response.data?.data };
    } catch (error) {
      return { success: false, error: getErrorMessage(error, 'Could not load delivery status') };
    }
  },

  /**
   * Run delivery now. Optional: { gitHubUsername } to correct the customer's username,
   * { productIds } to deliver a subset.
   * POST /api/fulfillment/order/{orderId}/retry -> data: { success, message, deliverablesAttached, repositoryAccessGranted, errors, status }
   */
  retryDelivery: async (orderId, options = {}) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.FULFILLMENT.RETRY(orderId), options);
      return { success: response.data?.data?.success === true, data: response.data?.data, message: response.data?.message };
    } catch (error) {
      return { success: false, error: getErrorMessage(error, 'Delivery retry failed') };
    }
  },

  /**
   * Expired access waiting for removal, removals that failed, and access removed in the last 30 days.
   * GET /api/fulfillment/access?limit -> { autoRevoke, count, waitingForRemoval, failed, items: [{ orderId, id, customer,
   * email, gitHubUsername, status: 'Expired' | 'RemovalFailed' | 'Removed',
   * deliverables: [{ productName, accessExpiresAt, accessRevokedAt, error }] }] }
   */
  getAccessChanges: async (limit = 100) => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.FULFILLMENT.ACCESS, { params: { limit } });
      return { success: true, data: response.data?.data };
    } catch (error) {
      return { success: false, error: getErrorMessage(error, 'Could not load expired access') };
    }
  },

  /** Ends access now: links stop opening and GitHub repository access is removed. */
  revokeAccess: async orderId => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.FULFILLMENT.ACCESS_REVOKE(orderId));
      return { success: true, data: response.data?.data, message: response.data?.message };
    } catch (error) {
      return { success: false, error: getErrorMessage(error, 'Could not remove access') };
    }
  },

  /** Extends access by `days`, or makes it lifetime. Removed GitHub access is granted again. */
  extendAccess: async (orderId, { days, lifetime = false }) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.FULFILLMENT.ACCESS_EXTEND(orderId), { days: days || 0, lifetime });
      return { success: true, data: response.data?.data, message: response.data?.message };
    } catch (error) {
      return { success: false, error: getErrorMessage(error, 'Could not extend access') };
    }
  },
};

export default fulfillmentService;
